import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Timer, Cpu, Sparkles, MapPin,
  Smartphone, Zap, UtensilsCrossed,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import useReveal from "../hooks/useReveal";
import { brand, navPaths, allfImages, allfRegions } from "../mock/mock";
import { useLang } from "../i18n/LanguageContext";
import Seo from "../components/Seo";

const pillarIcons = [Timer, Cpu, Sparkles, MapPin];
const procIcons = [Smartphone, Zap, UtensilsCrossed];

export default function RistoranteAllf() {
  const { t, lang } = useLang();
  useReveal([t]);
  const r = t.allfResto;
  const [active, setActive] = useState(0);
  const region = allfRegions[active];

  return (
    <main data-testid="ristorante-allf-page">
      <Seo
        title="Ristorante ALLF | Fast-casual italiano automatizzato, 20 regioni"
        description="ALLF, il ristorante italiano automatizzato: come un fast-food ma 100% cucina italiana, con 2 piatti simbolo per ognuna delle 20 regioni d'Italia."
        path="/ristorante-allf"
        image={allfImages.hero}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Restaurant",
          name: "ALLF",
          servesCuisine: "Italian",
          priceRange: "€",
          url: "https://www.michelchef.it/ristorante-allf",
          image: allfImages.hero,
          description: "Ristorante italiano fast-casual automatizzato con 2 piatti simbolo per ognuna delle 20 regioni d'Italia.",
          parentOrganization: { "@type": "Organization", name: "MichelChef® S.r.l." },
        }}
      />
      <PageHeader crumb={r.crumb} eyebrow={r.eyebrow} title={r.title} subtitle={r.subtitle} image={allfImages.hero} homeLabel={t.common.home} />

      {/* Quick actions */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 -mt-8 relative z-10">
        <div className="reveal flex flex-wrap gap-4 justify-center">
          <a href="#menu" className="btn-press inline-flex items-center gap-2 rounded-full bg-[#22B573] text-[#083a24] px-7 py-3.5 font-semibold hover:bg-[#1ba065] shadow-lg" data-testid="allf-explore-menu-btn">
            {r.menuTitle} <ArrowRight size={18} />
          </a>
          <Link to={navPaths.contact} className="btn-press inline-flex items-center gap-2 rounded-full bg-[#14110F] text-white px-7 py-3.5 font-semibold hover:bg-[#2a251f]">
            {r.ctaBtn} <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* CONCEPT */}
      <section className="py-24 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal">
            <span className="text-[#D8382E] font-semibold text-sm uppercase tracking-wider">{r.conceptEyebrow}</span>
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl mt-3 text-[#14110F]">{r.conceptTitle}</h2>
            <p className="mt-6 text-[#3B342E]/90 text-lg leading-relaxed">{r.conceptText}</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {r.pillars.map((p, i) => {
                const Icon = pillarIcons[i];
                return (
                  <div key={i} className="flex gap-3">
                    <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#17784A]/10 text-[#17784A] shrink-0"><Icon size={20} /></span>
                    <div>
                      <h3 className="font-display font-bold text-[#14110F]">{p.t}</h3>
                      <p className="text-sm text-[#3B342E]/70 mt-0.5 leading-relaxed">{p.d}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="reveal reveal-delay-1">
            <img src={allfImages.concept} alt="ALLF" loading="lazy" decoding="async" className="rounded-3xl w-full h-[480px] object-cover shadow-2xl" />
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-[#14110F] text-white py-24 grain">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="reveal text-center max-w-2xl mx-auto">
            <span className="text-[#22B573] font-semibold text-sm uppercase tracking-wider">{r.procEyebrow}</span>
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl mt-3">{r.procTitle}</h2>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {r.proc.map((s, i) => {
              const Icon = procIcons[i];
              return (
                <div key={i} className={`reveal reveal-delay-${i} rounded-2xl bg-white/5 border border-white/10 p-8 card-lift`}>
                  <div className="flex items-center justify-between">
                    <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#22B573] text-[#083a24]"><Icon size={24} /></span>
                    <span className="font-display font-extrabold text-3xl text-white/15">{s.n}</span>
                  </div>
                  <h3 className="font-display font-bold text-lg mt-5">{s.t}</h3>
                  <p className="mt-2 text-white/60 leading-relaxed">{s.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* REGIONAL MENU (interactive) */}
      <section id="menu" className="py-24 max-w-7xl mx-auto px-5 lg:px-8 scroll-mt-24">
        <div className="reveal text-center max-w-2xl mx-auto">
          <span className="text-[#D8382E] font-semibold text-sm uppercase tracking-wider">{r.menuEyebrow}</span>
          <h2 className="font-display font-extrabold text-3xl lg:text-4xl mt-3 text-[#14110F]">{r.menuTitle}</h2>
          <p className="mt-4 text-[#3B342E]/70">{r.menuNote}</p>
          <div className="mt-5 flex items-center justify-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#17784A]/10 text-[#17784A] px-3.5 py-1.5 text-sm font-semibold"><MapPin size={14} /> {r.regionsCount}</span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#D8382E]/10 text-[#D8382E] px-3.5 py-1.5 text-sm font-semibold"><UtensilsCrossed size={14} /> {r.dishesCount}</span>
          </div>
        </div>

        {/* Region selector */}
        <div className="reveal mt-12 flex flex-wrap justify-center gap-2.5">
          {allfRegions.map((rg, i) => (
            <button
              key={rg.region}
              onClick={() => setActive(i)}
              data-testid={`region-chip-${i}`}
              className={`btn-press rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                active === i
                  ? "bg-[#17784A] text-white shadow-md"
                  : "bg-white text-[#3B342E] border border-black/10 hover:border-[#17784A]/40 hover:text-[#17784A]"
              }`}
            >
              {rg.region}
            </button>
          ))}
        </div>

        {/* Selected region dishes */}
        <div className="mt-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#17784A] text-white"><MapPin size={22} /></span>
            <div>
              <div className="text-xs uppercase tracking-wider text-[#3B342E]/55">{r.signatureLabel}</div>
              <h3 className="font-display font-extrabold text-2xl text-[#14110F]" data-testid="active-region-name">{region.region}</h3>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {region.dishes.map((d, i) => (
              <div key={i} data-testid={`dish-card-${i}`} className="group bg-white rounded-2xl p-7 shadow-sm border border-black/5 card-lift hover:shadow-xl">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[#F3EFE7] text-[#17784A] group-hover:bg-[#17784A] group-hover:text-white transition-colors"><UtensilsCrossed size={22} /></span>
                    <h4 className="font-display font-bold text-lg text-[#14110F] leading-snug">{d.name}</h4>
                  </div>
                  <span className="font-display font-extrabold text-2xl text-[#22B573]/25">{i + 1}</span>
                </div>
                <p className="mt-4 text-[#3B342E]/75 leading-relaxed">{d.desc[lang]}</p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#17784A]">
                  <MapPin size={14} /> {region.region}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="reveal relative overflow-hidden rounded-[32px] bg-[#17784A] px-8 py-16 lg:p-20 grain">
          <div className="relative max-w-2xl">
            <h2 className="font-display font-extrabold text-white text-3xl lg:text-5xl tracking-tight">{r.ctaTitle}</h2>
            <p className="mt-5 text-white/80 text-lg">{r.ctaText}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to={navPaths.contact} className="btn-press inline-flex items-center gap-2 rounded-full bg-white text-[#14110F] px-7 py-3.5 font-semibold hover:bg-[#FBF9F4]" data-testid="allf-partner-cta">
                {r.ctaBtn} <ArrowRight size={18} />
              </Link>
              <a href={brand.allfUrl} target="_blank" rel="noreferrer" className="btn-press inline-flex items-center gap-2 rounded-full border border-white/40 text-white px-7 py-3.5 font-semibold hover:bg-white/10">
                {r.officialBtn} <ArrowUpRight size={18} />
              </a>
            </div>
          </div>
          <UtensilsCrossed size={260} className="absolute -right-10 -bottom-14 text-white/5" />
        </div>
      </section>
    </main>
  );
}
